<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-form :loading="saving || loadingRefs" @submit-valid="onSave">
    <oxd-form-row v-if="canChooseOwner">
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <employee-autocomplete
            v-model="form.employee"
            :rules="rules.employee"
            :params="{includeEmployees: 'onlyCurrent'}"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <template v-if="kind === 'RECEIPT'">
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.category"
              type="select"
              :label="$t('expensa.col_category')"
              :options="categoryOptions"
              :rules="rules.required"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.merchant"
              :label="$t('expensa.merchant')"
              :rules="rules.merchant"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="form.date"
              :label="$t('general.date')"
              :rules="rules.date"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.amount"
              :label="$t('general.amount')"
              :rules="rules.amount"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.currency"
              type="select"
              :label="$t('general.currency')"
              :options="currencyOptions"
              :rules="rules.required"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.vatRate"
              type="select"
              :label="$t('expensa.vat_rate')"
              :options="vatOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.documentType"
              type="select"
              :label="$t('expensa.document_type')"
              :options="documentOptions"
              :rules="rules.required"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.paymentMethod"
              type="select"
              :label="$t('expensa.payment_method')"
              :options="paymentOptions"
              :rules="rules.required"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.project"
              :label="$t('expensa.project')"
              :rules="rules.project"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.description"
          type="textarea"
          :label="$t('general.description')"
          :rules="rules.description"
        />
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.notes"
          type="textarea"
          :label="$t('general.notes')"
          :rules="rules.notes"
        />
      </oxd-form-row>
    </template>

    <template v-else-if="kind === 'MILEAGE'">
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.vehicle"
              type="select"
              :label="$t('expensa.vehicle')"
              :options="vehicleOptions"
              :rules="rules.required"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="form.date"
              :label="$t('general.date')"
              :rules="rules.date"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.routeFrom"
              :label="$t('expensa.route_from')"
              :rules="rules.route"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.routeTo"
              :label="$t('expensa.route_to')"
              :rules="rules.route"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.distance"
              :label="$t('expensa.distance_km')"
              :rules="rules.distance"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.roundTrip"
          type="checkbox"
          :true-value="true"
          :false-value="false"
          :option-label="$t('expensa.round_trip')"
        />
      </oxd-form-row>
      <oxd-form-row v-if="mileage">
        <oxd-text class="expensa-preview">
          {{ $t('expensa.distance_km') }}: {{ km(mileage.oneWayKm) }} km -
          {{ $t('expensa.total_km') }}: {{ km(mileage.totalKm) }} km -
          {{ $t('expensa.mileage_preview') }}:
          {{ eur(mileage.amount / 100) }}
        </oxd-text>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.description"
          type="textarea"
          :label="$t('general.description')"
          :rules="rules.description"
        />
      </oxd-form-row>
    </template>

    <template v-else>
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="form.startDate"
              :label="$t('expensa.trip_start')"
              :rules="rules.date"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.startTime"
              type="time"
              :label="$t('general.time')"
              :rules="rules.time"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="form.endDate"
              :label="$t('expensa.trip_end')"
              :rules="rules.date"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.endTime"
              type="time"
              :label="$t('general.time')"
              :rules="rules.endTime"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.country"
              :label="$t('expensa.trip_country')"
              :rules="rules.country"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item class="--span-column-2">
            <oxd-input-field
              v-model="form.destination"
              :label="$t('expensa.trip_destination')"
              :rules="rules.destination"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.meals"
              type="checkbox"
              :true-value="true"
              :false-value="false"
              :option-label="$t('expensa.meals_provided')"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.lodging"
              type="checkbox"
              :true-value="true"
              :false-value="false"
              :option-label="$t('expensa.lodging_provided')"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.description"
          type="textarea"
          :label="$t('general.description')"
          :rules="rules.description"
        />
      </oxd-form-row>
    </template>

    <oxd-divider />
    <oxd-form-actions>
      <required-text />
      <oxd-button
        type="button"
        display-type="ghost"
        :label="$t('general.cancel')"
        @click="$emit('cancel')"
      />
      <oxd-button
        type="submit"
        display-type="secondary"
        :label="expense ? $t('general.save') : $t('expensa.save_expense')"
        :disabled="saving"
      />
    </oxd-form-actions>
  </oxd-form>
</template>

<script>
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  required,
  shouldNotExceedCharLength,
  validDateFormat,
  validSelection,
} from '@/core/util/validation/rules';
import {expensaApi, errorMessage} from '../util/api';
import {dateTimeToIso, eur, moneyToApi} from '../util/format';
import {mileagePreview} from '../util/mileage';

const VAT_RATES = [0, 4, 5, 10, 22];
const DOCUMENT_TYPES = ['SCONTRINO', 'FATTURA', 'RICEVUTA', 'ALTRO'];
const PAYMENT_METHODS = ['PERSONAL', 'COMPANY_CARD', 'CASH'];
const KM_PATTERN = /^\d{1,4}(?:[.,]\d{1,2})?$/;

// Identificativo per l'idempotenza della creazione: lo stesso valore viene
// rinviato se l'utente riprova dopo un errore di rete.
const newClientId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const textOrNull = (value) => {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
};

// Parte "YYYY-MM-DD" e "HH:mm" di un istante ISO (ora italiana del server).
const splitIso = (iso) =>
  iso ? [iso.slice(0, 10), iso.slice(11, 16)] : [null, null];

export default {
  name: 'ExpensaExpenseForm',
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },
  props: {
    kind: {
      type: String,
      required: true,
    },
    // Spesa in modifica (vista di dettaglio); assente per una nuova spesa.
    expense: {
      type: Object,
      default: null,
    },
  },
  emits: ['saved', 'cancel'],
  setup() {
    const {userDateFormat} = useDateFormat();
    return {userDateFormat};
  },
  data() {
    return {
      loadingRefs: true,
      saving: false,
      me: null,
      categories: [],
      vehicles: [],
      currencies: ['EUR'],
      clientId: newClientId(),
      initial: null,
      form: {
        employee: null,
        category: null,
        merchant: '',
        amount: '',
        currency: null,
        date: null,
        vatRate: null,
        documentType: null,
        paymentMethod: null,
        description: '',
        project: '',
        notes: '',
        vehicle: null,
        routeFrom: '',
        routeTo: '',
        distance: '',
        roundTrip: true,
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        country: 'IT',
        destination: '',
        meals: false,
        lodging: false,
      },
    };
  },
  computed: {
    rules() {
      return {
        required: [required],
        employee: [validSelection],
        merchant: [shouldNotExceedCharLength(200)],
        description: [shouldNotExceedCharLength(500)],
        project: [shouldNotExceedCharLength(100)],
        notes: [shouldNotExceedCharLength(2000)],
        route: [required, shouldNotExceedCharLength(255)],
        destination: [required, shouldNotExceedCharLength(255)],
        date: [required, validDateFormat(this.userDateFormat)],
        time: [required],
        endTime: [required, this.endAfterStart],
        amount: [
          required,
          (v) => moneyToApi(v) !== null || this.$t('expensa.invalid_amount'),
        ],
        distance: [
          required,
          (v) =>
            (KM_PATTERN.test((v ?? '').trim()) && this.parseKm(v) > 0) ||
            this.$t('expensa.invalid_km'),
        ],
        country: [
          required,
          (v) =>
            /^[A-Z]{2}$/.test(v ?? '') || this.$t('expensa.invalid_country'),
        ],
      };
    },
    canChooseOwner() {
      return !this.expense && !!this.me && this.me.isFinance;
    },
    ownerEmpNumber() {
      if (this.expense) return this.expense.empNumber;
      if (this.form.employee) return this.form.employee.id;
      return this.me ? this.me.empNumber : null;
    },
    categoryOptions() {
      const options = this.categories
        .filter((c) => c.active !== false)
        .map((c) => ({id: c.id, label: c.name}));
      // Una categoria disattivata dopo l'inserimento resta visibile in modifica.
      const current = this.expense && this.expense.categoryId;
      if (current && !options.some((o) => o.id === current)) {
        options.push({id: current, label: this.expense.categoryName || '—'});
      }
      return options;
    },
    currencyOptions() {
      return this.currencies.map((c) => ({id: c, label: c}));
    },
    vatOptions() {
      return [
        {id: null, label: '—'},
        ...VAT_RATES.map((r) => ({id: r, label: `${r}%`})),
      ];
    },
    documentOptions() {
      return DOCUMENT_TYPES.map((id) => ({
        id,
        label: this.$t(`expensa.doc_${id.toLowerCase()}`),
      }));
    },
    paymentOptions() {
      return PAYMENT_METHODS.map((id) => ({
        id,
        label: this.$t(`expensa.pay_${id.toLowerCase()}`),
      }));
    },
    vehicleOptions() {
      const options = this.vehicles
        .filter((v) => v.company || v.empNumber === this.ownerEmpNumber)
        .map((v) => ({
          id: v.id,
          label: v.plate ? `${v.name} - ${v.plate}` : v.name,
        }));
      // Il veicolo della spesa resta scelto anche se non è più elencato.
      const current = this.expense && this.expense.mileage;
      if (current && current.vehicleId) {
        if (!options.some((o) => o.id === current.vehicleId)) {
          options.push({
            id: current.vehicleId,
            label: `${this.$t('expensa.vehicle')} #${current.vehicleId}`,
          });
        }
      }
      return options;
    },
    // Tariffa al km in uso: lo snapshot della spesa se il veicolo non cambia.
    ratePerKm() {
      const id = this.form.vehicle && this.form.vehicle.id;
      if (!id) return null;
      if (
        this.expense &&
        this.expense.mileage &&
        this.expense.mileage.vehicleId === id &&
        this.expense.mileage.ratePerKm
      ) {
        return Number(this.expense.mileage.ratePerKm);
      }
      const vehicle = this.vehicles.find((v) => v.id === id);
      return vehicle && vehicle.ratePerKm ? Number(vehicle.ratePerKm) : null;
    },
    mileage() {
      const km = this.parseKm(this.form.distance);
      if (this.kind !== 'MILEAGE' || !(km > 0) || this.ratePerKm === null) {
        return null;
      }
      return mileagePreview(km, this.ratePerKm, this.form.roundTrip);
    },
  },
  watch: {
    // Cambiando titolare (finance) un veicolo personale altrui non vale più.
    ownerEmpNumber() {
      const v = this.form.vehicle;
      if (v && !this.vehicleOptions.some((o) => o.id === v.id)) {
        this.form.vehicle = null;
      }
    },
  },
  async beforeMount() {
    try {
      const me = await expensaApi('/me').getAll();
      this.me = me.data.data;
      const [categories, vehicles, currencies] = await Promise.all([
        expensaApi('/categories').getAll(),
        expensaApi('/vehicles').getAll(this.me.isFinance ? {} : {mine: true}),
        expensaApi('/fx/currencies').getAll(),
      ]);
      this.categories = categories.data.data;
      this.vehicles = vehicles.data.data;
      this.currencies = currencies.data.data;
      this.prefill();
    } catch (e) {
      this.$toast.error({
        title: this.$t('general.error'),
        message: errorMessage(e),
      });
    } finally {
      this.loadingRefs = false;
    }
  },
  methods: {
    eur,
    km(value) {
      return value.toFixed(1).replace('.', ',');
    },
    parseKm(text) {
      const value = (text ?? '').trim();
      return KM_PATTERN.test(value) ? Number(value.replace(',', '.')) : NaN;
    },
    endAfterStart() {
      const start = dateTimeToIso(this.form.startDate, this.form.startTime);
      const end = dateTimeToIso(this.form.endDate, this.form.endTime);
      if (start === null || end === null) return true;
      return (
        new Date(end).getTime() > new Date(start).getTime() ||
        this.$t('expensa.end_after_start')
      );
    },
    option(options, id) {
      return options.find((o) => o.id === id) || null;
    },
    prefill() {
      const e = this.expense;
      const f = this.form;
      if (!e) {
        f.currency = this.option(this.currencyOptions, 'EUR');
        f.documentType = this.option(this.documentOptions, 'SCONTRINO');
        f.paymentMethod = this.option(this.paymentOptions, 'PERSONAL');
        const now = new Date();
        f.date = [
          now.getFullYear(),
          String(now.getMonth() + 1).padStart(2, '0'),
          String(now.getDate()).padStart(2, '0'),
        ].join('-');
        this.initial = null;
        return;
      }
      f.category = this.option(this.categoryOptions, e.categoryId);
      f.merchant = e.merchant || '';
      f.amount = e.amount || '';
      f.currency = this.option(this.currencyOptions, e.currency || 'EUR');
      f.date = e.date;
      f.vatRate =
        e.vatRate === null || e.vatRate === undefined
          ? null
          : this.option(this.vatOptions, Number(e.vatRate));
      f.documentType = this.option(this.documentOptions, e.documentType);
      f.paymentMethod = this.option(this.paymentOptions, e.paymentMethod);
      f.description = e.description || '';
      f.project = e.project || '';
      f.notes = e.notes || '';
      if (e.mileage) {
        f.vehicle = this.option(this.vehicleOptions, e.mileage.vehicleId);
        f.routeFrom = e.mileage.routeFrom || '';
        f.routeTo = e.mileage.routeTo || '';
        f.distance = e.mileage.distanceKm ? String(e.mileage.distanceKm) : '';
        f.roundTrip = !!e.mileage.roundTrip;
      }
      if (e.perDiem) {
        [f.startDate, f.startTime] = splitIso(e.perDiem.tripStart);
        [f.endDate, f.endTime] = splitIso(e.perDiem.tripEnd);
        f.country = e.perDiem.country || 'IT';
        f.destination = e.perDiem.destination || '';
        f.meals = !!e.perDiem.mealsProvided;
        f.lodging = !!e.perDiem.lodgingProvided;
      }
      this.initial = this.payload();
    },
    // Campi dell'API per il tipo di spesa, a partire dal form.
    payload() {
      const f = this.form;
      if (this.kind === 'MILEAGE') {
        const km = this.parseKm(f.distance);
        return {
          vehicleId: f.vehicle ? f.vehicle.id : null,
          routeFrom: textOrNull(f.routeFrom),
          routeTo: textOrNull(f.routeTo),
          distanceKm:
            km > 0 ? mileagePreview(km, 0, false).oneWayKm.toFixed(1) : null,
          roundTrip: f.roundTrip,
          date: f.date,
          description: textOrNull(f.description),
        };
      }
      if (this.kind === 'PER_DIEM') {
        return {
          tripStart: dateTimeToIso(f.startDate, f.startTime),
          tripEnd: dateTimeToIso(f.endDate, f.endTime),
          tripCountry: textOrNull(f.country),
          tripDestination: textOrNull(f.destination),
          mealsProvided: f.meals,
          lodgingProvided: f.lodging,
          description: textOrNull(f.description),
        };
      }
      return {
        categoryId: f.category ? f.category.id : null,
        merchant: textOrNull(f.merchant),
        amount: moneyToApi(f.amount),
        currency: f.currency ? f.currency.id : null,
        date: f.date,
        vatRate: f.vatRate ? f.vatRate.id : null,
        documentType: f.documentType ? f.documentType.id : null,
        paymentMethod: f.paymentMethod ? f.paymentMethod.id : null,
        description: textOrNull(f.description),
        project: textOrNull(f.project),
        notes: textOrNull(f.notes),
      };
    },
    // PUT parziale: solo i campi cambiati rispetto a quelli caricati, quindi
    // categoryId e vehicleId partono solo se sono cambiati.
    changes() {
      const now = this.payload();
      const changed = {};
      Object.keys(now).forEach((key) => {
        if (now[key] !== this.initial[key]) changed[key] = now[key];
      });
      return changed;
    },
    async onSave() {
      this.saving = true;
      try {
        if (this.expense) {
          const changed = this.changes();
          if (Object.keys(changed).length === 0) {
            this.$emit('cancel');
            return;
          }
          const {data} = await expensaApi('/expenses').update(
            this.expense.id,
            changed,
          );
          this.$toast.success({
            title: this.$t('general.success'),
            message: this.$t('expensa.toast_expense_saved'),
          });
          this.$emit('saved', data.data);
          return;
        }
        const body = {kind: this.kind, clientId: this.clientId};
        const fields = this.payload();
        Object.keys(fields).forEach((key) => {
          if (fields[key] !== null) body[key] = fields[key];
        });
        const owner = this.form.employee ? this.form.employee.id : null;
        if (owner && this.me && owner !== this.me.empNumber) {
          body.empNumber = owner;
        }
        const {data} = await expensaApi('/expenses').create(body);
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_expense_created'),
        });
        this.$emit('saved', data.data);
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-preview {
  font-weight: 600;
}
</style>
